// A 512-number visual descriptor of a product photo — the LOCAL REFERENCE
// provider for image search (supabase/functions/market-image-search). No model,
// no network, no cost: colour and shape statistics any browser can compute.
//
//   72  colour histogram (8 hue × 3 saturation × 3 value), centre-weighted,
//       near-white and near-black background pixels down-weighted
//   48  colour layout: 4×4 grid × mean (Y, Cb, Cr)
//  128  shape: 4×4 grid × 8 gradient orientations (magnitude-weighted)
//  256  a 16×16 grayscale thumbnail, zero-mean
//    8  zeros (to 512)
// Each block is L2-normalized and weighted, then the whole vector is
// normalized, so cosine similarity = dot product.
//
// It finds the same photo, re-crops and close variants well; it does NOT
// understand what a product is the way a learned embedding does. Production
// uses a learned multimodal embedding (see the function's README); this file
// keeps the whole path testable locally and stays the fallback contract.
//
// Pure: no imports, no DOM (scripts evaluate it in a page as is).

export const DESCRIPTOR_MODEL = 'maabar-descriptor-v1';
export const DESCRIPTOR_SIZE = 32;   // the image is reduced to 32×32 (centre square) before this runs

const norm = (v, w = 1) => { let s = 0; for (const x of v) s += x * x; s = Math.sqrt(s) || 1; return v.map((x) => (x / s) * w); };

/** rgba: Uint8ClampedArray of a 32×32 RGBA image (row-major). → number[512] */
export function descriptorFromRgba(rgba, size = DESCRIPTOR_SIZE) {
  const n = size * size;
  const hist = new Array(72).fill(0);
  const layout = new Array(48).fill(0), layoutN = new Array(16).fill(0);
  const gray = new Float64Array(n);
  const c = (size - 1) / 2;
  for (let i = 0; i < n; i++) {
    const r = rgba[i * 4] / 255, g = rgba[i * 4 + 1] / 255, b = rgba[i * 4 + 2] / 255;
    const x = i % size, y = Math.floor(i / size);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    let h = 0;
    if (d > 0) h = max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    const s = max === 0 ? 0 : d / max, v = max;
    // background (white / black, unsaturated) counts a little; the centre counts more
    const bg = s < 0.12 && (v > 0.9 || v < 0.08) ? 0.15 : 1;
    const centre = 1 - 0.5 * (Math.hypot(x - c, y - c) / Math.hypot(c, c));
    const bin = Math.min(7, Math.floor(h / 6 * 8)) * 9 + Math.min(2, Math.floor(s * 3)) * 3 + Math.min(2, Math.floor(v * 3));
    hist[bin] += bg * centre;
    const Y = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = Y;
    const cell = Math.floor(y / (size / 4)) * 4 + Math.floor(x / (size / 4));
    layout[cell * 3] += Y; layout[cell * 3 + 1] += b - Y; layout[cell * 3 + 2] += r - Y; layoutN[cell]++;
  }
  for (let k = 0; k < 16; k++) for (let j = 0; j < 3; j++) layout[k * 3 + j] /= layoutN[k] || 1;
  const edges = new Array(128).fill(0);
  for (let y = 1; y < size - 1; y++) for (let x = 1; x < size - 1; x++) {
    const gx = gray[y * size + x + 1] - gray[y * size + x - 1];
    const gy = gray[(y + 1) * size + x] - gray[(y - 1) * size + x];
    const m = Math.hypot(gx, gy);
    if (m < 1e-3) continue;
    const o = Math.min(7, Math.floor(((Math.atan2(gy, gx) + Math.PI) / (2 * Math.PI)) * 8));
    const cell = Math.floor(y / (size / 4)) * 4 + Math.floor(x / (size / 4));
    edges[cell * 8 + o] += m;
  }
  const thumb = new Array(256).fill(0);
  const f = size / 16;
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
    let s = 0; for (let dy = 0; dy < f; dy++) for (let dx = 0; dx < f; dx++) s += gray[(y * f + dy) * size + x * f + dx];
    thumb[y * 16 + x] = s / (f * f);
  }
  const mean = thumb.reduce((a, b) => a + b, 0) / 256;
  const v = [...norm(hist, 1.0), ...norm(layout, 0.6), ...norm(edges, 1.0), ...norm(thumb.map((t) => t - mean), 0.8), ...new Array(8).fill(0)];
  return norm(v).map((x) => Math.round(x * 1e6) / 1e6);
}

/** Cosine similarity of two descriptors (both already normalized). */
export const similarity = (a, b) => { let s = 0; for (let i = 0; i < a.length; i++) s += a[i] * b[i]; return s; };

/**
 * Browser step shared by the search page and the catalog indexer: the centre
 * square of an image (ImageBitmap / HTMLImageElement / canvas), drawn at 32×32,
 * as RGBA. Both sides MUST use this one function, or their numbers drift.
 */
export function rgbaFromImage(source, width, height, doc) {
  const side = Math.min(width, height);
  const canvas = doc.createElement('canvas');
  canvas.width = DESCRIPTOR_SIZE; canvas.height = DESCRIPTOR_SIZE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, DESCRIPTOR_SIZE, DESCRIPTOR_SIZE);
  ctx.drawImage(source, (width - side) / 2, (height - side) / 2, side, side, 0, 0, DESCRIPTOR_SIZE, DESCRIPTOR_SIZE);
  return ctx.getImageData(0, 0, DESCRIPTOR_SIZE, DESCRIPTOR_SIZE).data;
}
